/**
 * Google Analytics, on the website only.
 *
 * The iOS app must never load this. It has its own analytics and its own
 * privacy disclosures, and a second measurement SDK inside the WKWebView would
 * double-count every session and put a web cookie identifier on a device that
 * already reports through native. Two gates keep it out:
 *
 *   1. REACT_APP_EMBEDDED, set for the app build. Inlined at build time, so the
 *      whole branch below is dropped by the minifier and the firebase chunk is
 *      never even emitted into that bundle.
 *   2. A live check for the native message handlers, which catches the case the
 *      flag cannot: the plain web build loaded inside the app.
 *
 * The SDK is imported dynamically so it lands in its own chunk. Firebase is
 * several hundred KB unpacked, and it should not sit in the path between a
 * player opening the page and seeing a board.
 *
 * The apiKey here is not a secret. Firebase web keys identify the project, they
 * do not authorise anything, and access is controlled by the project's own
 * rules - which is why Google ships them in client code.
 */
const firebaseConfig = {
  apiKey: "AIzaSyDm_2mEQCwy5uIuXRLt8QKTMQkkNOZZEcI",
  authDomain: "total-ten-game.firebaseapp.com",
  projectId: "total-ten-game",
  storageBucket: "total-ten-game.firebasestorage.app",
  messagingSenderId: "1030183228090",
  appId: "1:1030183228090:web:c93ef7d856b83dfabe2883",
  measurementId: "G-MRK4N95800",
};

const isAppBuild = process.env.REACT_APP_EMBEDDED === 'true';
// Screenshot builds play the game for real. Those sessions are not players.
const isDemoBuild = process.env.REACT_APP_DEMO === 'true';

/** Inside the iOS app, whichever build got loaded there */
const inNativeApp = () => Boolean(window.webkit?.messageHandlers);

/**
 * Starts Firebase Analytics if this is the website. Safe to call anywhere -
 * it resolves to false and does nothing everywhere else.
 * @returns {Promise<boolean>} whether analytics actually started
 */
export async function startAnalytics() {
  if (isAppBuild || isDemoBuild || inNativeApp()) return false;

  try {
    const [{ initializeApp }, { getAnalytics, isSupported }] = await Promise.all([
      import('firebase/app'),
      import('firebase/analytics'),
    ]);

    // Measurement needs cookies and IndexedDB. Private windows and some
    // in-app browsers have neither, and getAnalytics throws rather than
    // degrading, so ask before calling it.
    if (!(await isSupported())) return false;

    getAnalytics(initializeApp(firebaseConfig));
    return true;
  } catch (error) {
    // A blocked or failed analytics load is not a reason to break the game
    console.error('Analytics did not start:', error);
    return false;
  }
}
